import React, { useState, useEffect, useRef } from 'react';
import FormActionButtons from '../Assets/FormActionButtons';
import { useTranslation } from 'react-i18next';

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: 'user' | 'admin';
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: UserFormData) => void;
  initialData?: Partial<UserFormData>;
  isEditMode?: boolean;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = {},
  isEditMode = false,
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });

  const prevIsOpenRef = useRef<boolean>(false);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
    });
  };

  useEffect(() => {
    if (!prevIsOpenRef.current && isOpen) {
      if (isEditMode && initialData) {
        setFormData({
          ...initialData,
          password: '',
        } as UserFormData);
      } else {
        resetForm();
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, isEditMode, initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    resetForm();
  };

  const isFormValid = () => {
    const { name, email, password } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      name.trim() !== '' &&
      emailRegex.test(email) &&
      (isEditMode || password.trim() !== '')
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-xl font-montserrat">
        <h2
          className={
            isEditMode
              ? "text-xl font-semibold mb-4 text-center text-brand-800"
              : "text-2xl font-semibold mb-6 text-brand-800 text-center"
          }
        >
          {isEditMode ? t('userModal.titleEdit') : t('userModal.titleCreate')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block mb-1 font-medium text-gray-700">
              {t('userModal.labels.name')}
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('userModal.labels.name')}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block mb-1 font-medium text-gray-700">
              {t('userModal.labels.email')}
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('userModal.labels.email')}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
              required
            />
          </div>

          {!isEditMode && (
            <div>
              <label htmlFor="password" className="block mb-1 font-medium text-gray-700">
                {t('userModal.labels.password')}
              </label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('userModal.labels.password')}
                className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
                required
              />
            </div>
          )}
          <div>
            <label htmlFor="role" className="block mb-1 font-medium text-gray-700">
              {t('userModal.labels.role')}

            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <FormActionButtons
            edit={isEditMode}
            onClose={handleClose}
            onSubmit={handleSubmit}
            disabled={!isFormValid()}
          />
        </form>
      </div>
    </div>
  );

};

export default UserModal;
